// Saved social posts. The whole storage layer is `createRecordStore` — this file
// is the ~20 minutes the reusable store was built to save, versus the couple of
// hours the Offer Ladder took.
//
// Until now the generator was ephemeral by design: one post in React state,
// replaced on the next generate. That's the actual pain — "Generate another"
// destroys the post you liked. Saving is a deliberate button, not automatic, so
// the list holds keepers rather than every experiment.

import { createRecordStore } from "./recordStore";

// `id`, `createdAt`, `updatedAt` are handled by recordStore — not listed.
// `inputs` / `visual` are the jsonb columns added in 0001; `title` came in 0002.
// `arcId` is unused until multi-day arcs land, but round-trips now so a post saved
// today can be adopted into an arc later without a migration.
// `captionDraft` is what Brill wrote, captured at generation and never rewritten.
// `caption` is what she kept. The gap between them is the ONLY signal in the app
// about her actual voice — every other field is a choice she made from a menu.
// It exists now, ahead of the per-channel learned-voice work, because an edit made
// today can't be recovered later: the edit overwrites the draft in place, and there
// is no backfill. Costs one nullable column and nothing at render time.
export const SOCIAL_POST_FIELDS = [
  // `ctaId` points at a row in `ctas`. FK is ON DELETE SET NULL, so deleting a
  // CTA leaves the post intact and simply unpointed rather than dangling.
  "title", "arcId", "ctaId", "platform", "caption", "captionDraft", "inputs", "visual",
  "scheduledDate", "status", "source", "sourceSection", "sourceText", "sourceHash",
  // 0015: which offer this post serves — the connector, read by "Supported by".
  "offerId",
  // 0032: the first comment she pastes under the post (usually the link). Its
  // own field because it goes somewhere of its own, with its own Copy.
  "firstComment",
];

export const useSocialPosts = createRecordStore({
  table: "social_posts",
  fields: SOCIAL_POST_FIELDS,
  idPrefix: "post",
});

/**
 * A row label from the post body. `caption` is the entire post, which is far too
 * long for a table cell, so the first meaningful line becomes the title.
 *
 * Derived once at save time rather than computed on read: she can rename a post,
 * and a value recomputed from the caption would silently overwrite that whenever
 * she edited the body.
 */
export function deriveTitle(caption) {
  const firstLine = (caption || "")
    .split("\n")
    .map((l) => l.trim())
    // Skip blank lines and lines that are only punctuation or emoji separators,
    // which some hook formulas open with.
    .find((l) => l.length > 1 && /[a-z0-9]/i.test(l));
  if (!firstLine) return "Untitled post";
  const clean = firstLine.replace(/^[#>*\-\s]+/, "").replace(/\*\*/g, "").trim();
  return clean.length > 70 ? clean.slice(0, 70).trimEnd() + "…" : clean || "Untitled post";
}
